<template>
  <div>
    <!-- Error Alert -->
    <b-alert
      v-if="errors"
      variant="danger"
      show
      dismissible
      class="mb-3"
      @dismissed="errors = ''"
    >
      {{ errors }}
    </b-alert>

    <b-row>
      <b-col md="3">
        <b-form-group label="Item Name">
          <custom-v-select
            v-model="selectedProduct"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="filterProductOptions"
            :loading="productLoading"
            label="name"
            placeholder="Search Item Name"
            :deselect-from-dropdown="true"
            :filterable="false"
            @search="searchProducts"
            @input="handleProductChange"
            @open="onDropdownOpen"
          />
        </b-form-group>
      </b-col>
      <b-col md="3">
        <b-form-group label="Company">
          <custom-v-select
            v-model="selectedCompanies"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="companyOptions"
            :loading="companyOptionsLoading"
            label="text"
            placeholder="Select Company"
            :close-on-select="false"
            :deselect-from-dropdown="true"
            @input="filtersChangeHandler"
          />
        </b-form-group>
      </b-col>
      <b-col md="2">
        <b-form-group label="Product Name">
          <b-form-input
            v-model="productName"
            type="text"
            placeholder="Search Product Name"
            @input="debouncedFiltersChange"
          />
        </b-form-group>
      </b-col>
      <b-col md="2">
        <b-form-group label="Status">
          <custom-v-select
            v-model="selectedStatusOption"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="statusOptions"
            label="text"
            placeholder="Select Option"
            @input="filtersChangeHandler"
          />
        </b-form-group>
      </b-col>
      <b-col md="2">
        <b-form-group label="Has Batch">
          <custom-v-select
            v-model="selectedHasErpProduct"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="hasErpProductOptions"
            label="text"
            placeholder="Select Option"
            @input="filtersChangeHandler"
          />
        </b-form-group>
      </b-col>
      <b-col md="3">
        <b-form-group label="Discount Range">
          <b-row>
            <b-col cols="6">
              <b-form-input
                v-model.number="minDiscount"
                type="number"
                min="0"
                max="100"
                step="0.5"
                placeholder="Min %"
                @input="debouncedFiltersChange"
              />
            </b-col>
            <b-col cols="6">
              <b-form-input
                v-model.number="maxDiscount"
                type="number"
                min="0"
                max="100"
                step="0.5"
                placeholder="Max %"
                @input="debouncedFiltersChange"
              />
            </b-col>
          </b-row>
        </b-form-group>
      </b-col>
      <b-col md="3">
        <b-form-group label="Start Date">
          <flat-picker
            v-model="startDateRange"
            placeholder="Select Date Range"
            :config="{ mode: 'range' }"
          />
        </b-form-group>
      </b-col>
      <b-col md="3">
        <b-form-group label="End Date">
          <flat-picker
            v-model="endDateRange"
            placeholder="Select Date Range"
            :config="{ mode: 'range' }"
          />
        </b-form-group>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  VBTooltip, BRow, BCol, BFormGroup, BFormInput, BAlert,
} from 'bootstrap-vue'
import axios from 'axios'
import CustomVSelect from '@/components/UI/CustomVSelect.vue'
import FlatPicker from '@/components/UI/FlatPicker.vue'
import dateMixin from '@/mixins/dateMixin'

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BAlert,
    CustomVSelect,
    FlatPicker,
  },
  mixins: [dateMixin],
  data() {
    return {
      errors: '',
      selectedProduct: '',
      productOptions: [],
      filterProductOptions: [],
      allProducts: [],
      productLoading: false,
      debounceTimer: null,
      searchTimeout: null,
      productName: '',
      minDiscount: null,
      maxDiscount: null,
      selectedStatusOption: [],
      selectedHasErpProduct: [],
      selectedCompanies: [],
      companyOptions: [],
      companyOptionsLoading: false,
      statusOptions: [
        { text: 'Active', value: true },
        { text: 'InActive', value: 'false' },
      ],
      hasErpProductOptions: [
        { text: 'Yes', value: true },
        { text: 'No', value: false },
      ],
      startDateRange: null,
      endDateRange: null,
    }
  },
  watch: {
    startDateRange(newValue) {
      if (newValue) {
        const [fromDate, toDate] = newValue.split(' to ')
        if (fromDate && toDate) {
          this.filtersChangeHandler()
        }
      } else if (newValue === '') {
        this.filtersChangeHandler()
      }
    },
    endDateRange(newValue) {
      if (newValue) {
        const [fromDate, toDate] = newValue.split(' to ')
        if (fromDate && toDate) {
          this.filtersChangeHandler()
        }
      } else if (newValue === '') {
        this.filtersChangeHandler()
      }
    },
  },
  async created() {
    await Promise.all([
      this.fetchAllProducts(),
      this.fetchCompanyOptions(),
    ])
  },
  beforeDestroy() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  },
  methods: {
    debouncedFiltersChange() {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer)
      }
      this.debounceTimer = setTimeout(() => {
        this.filtersChangeHandler()
      }, 500)
    },

    async fetchAllProducts() {
      this.productLoading = true
      try {
        const productRes = await axios.get('/marg/product_options')
        this.allProducts = productRes.data.data.map(product => ({
          id: product.code,
          code: product.code,
          name: product.name,
          display_text: product.name,
          rate: parseFloat(product.rate),
          mrp: parseFloat(product.mrp),
        }))
        this.productOptions = this.allProducts
        this.filterProductOptions = this.allProducts.slice(0, 15)
        this.productLoading = false
      } catch (error) {
        this.errors = error.response?.data?.error || 'Failed to fetch products. Please try again.'
        this.productOptions = []
        this.allProducts = []
        this.filterProductOptions = []
        this.productLoading = false
      }
    },

    async fetchCompanyOptions() {
      this.companyOptionsLoading = true
      try {
        const companyRes = await axios.get('/company_options')
        this.companyOptions = companyRes.data.data.map(item => ({ text: item.text, id: item.text }))
      } catch {
        this.companyOptions = []
      } finally {
        this.companyOptionsLoading = false
      }
    },

    onDropdownOpen() {
      if (!this.filterProductOptions.length && this.allProducts.length) {
        this.filterProductOptions = this.allProducts.slice(0, 15)
      }
    },

    searchProducts(query) {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }
      this.searchTimeout = setTimeout(() => {
        if (!query || query.trim() === '') {
          this.filterProductOptions = this.allProducts.slice(0, 15)
        } else {
          const searchString = query.toLowerCase()
          const filtered = this.allProducts.filter(product => product.name.toLowerCase().includes(searchString))
          this.filterProductOptions = filtered.slice(0, 50)
        }
      }, 200)
    },

    handleProductChange(selectedProduct) {
      this.selectedProduct = selectedProduct || []
      this.filtersChangeHandler()
    },

    filtersChangeHandler() {
      const filtersdata = {}

      if (this.selectedProduct) {
        filtersdata.product_code = this.selectedProduct.code
      }

      if (this.productName && this.productName.trim()) {
        filtersdata.product_name = this.productName.trim()
      }
      if (this.selectedCompanies) {
        filtersdata.company = this.selectedCompanies.id
      }

      if (this.selectedStatusOption) {
        filtersdata.is_active = this.selectedStatusOption.value
      }

      if (this.selectedHasErpProduct) {
        filtersdata.has_erp_product = this.selectedHasErpProduct.value
      }

      // Handle discount range
      if (this.minDiscount !== null) {
        filtersdata.min_discount = this.minDiscount
      }
      if (this.maxDiscount !== null) {
        filtersdata.max_discount = this.maxDiscount
      }

      if (this.startDateRange) {
        const [startDateFrom, startDateTo] = this.startDateRange.split(' to ')
        if (startDateFrom && startDateTo) {
          filtersdata.start_date_min = startDateFrom
          filtersdata.start_date_max = startDateTo
        }
      }

      if (this.endDateRange) {
        const [endDateFrom, endDateTo] = this.endDateRange.split(' to ')
        if (endDateFrom && endDateTo) {
          filtersdata.end_date_min = endDateFrom
          filtersdata.end_date_max = endDateTo
        }
      }

      this.$emit('filtersUpdated', filtersdata)
    },
  },
}
</script>
